import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CTAButton from '../components/CTAButton';
import MetricsStrip from '../components/MetricsStrip';
import { SERVICES } from '../config/business';
import { MEDIA } from '../config/media';
import SEO from '../components/SEO';

const { FiArrowRight, FiInfo, FiTrendingUp, FiZap, FiLayers, FiShield, FiTarget } = FiIcons;

function Services() {
  const serviceMediaMap = [
    MEDIA.services.appointment,
    MEDIA.services.chatbot,
    MEDIA.services.crm,
    MEDIA.services.website,
    MEDIA.services.process,
    MEDIA.services.dashboard,
    MEDIA.services.documents,
    MEDIA.services.microsoft
  ];

  return (
    <main className="bg-white text-left">
      <SEO 
        title="Automation Services for Growing Australian Businesses | BPA Solutions" 
        description="Comprehensive automation systems and professional website design for Australian service businesses. Sydney-based team serving clients all over Australia." 
      />
      
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">Automation Services for Growing Australian Businesses</h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-3xl mx-auto px-4">
              Practical systems and high-conversion websites built for Australian small businesses. Explore our core service offerings below. ABN-registered Sydney-based Australian business.
            </p>
            <div className="flex justify-center px-4">
              <CTAButton size="lg" className="w-full sm:w-auto">Book a Free Consult</CTAButton>
            </div>
          </motion.div>
        </div>
      </section>

      <MetricsStrip />

      <section className="py-16 lg:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Business Automation and Website Services</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">Choose a practical solution for the bottleneck you want to remove first.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          {SERVICES.map((service, index) => (
            <article key={service.id} className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col">
              <Link to={`/services/${service.slug}`} className="flex flex-col h-full">
                <div className="h-48 overflow-hidden relative">
                  <img src={serviceMediaMap[index].src} alt={service.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" width="400" height="300" />
                </div>
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-tight">{service.name}</h3>
                    <SafeIcon icon={FiInfo} className="text-slate-400 h-5 w-5 shrink-0 ml-2" />
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 flex items-center space-x-3">
                    <SafeIcon icon={FiTrendingUp} className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-bold text-blue-900">{service.proof}</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-6 line-clamp-3 leading-relaxed">{service.description}</p>
                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-100">
                    <span className="text-blue-600 font-bold text-xs">From {service.startingPrice}</span>
                    <span className="text-slate-400 group-hover:text-blue-600 transition-colors flex items-center text-xs font-bold">
                      View Details <SafeIcon icon={FiArrowRight} className="ml-1 h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">Why Choose Our Small Business Automation Services?</h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                In the Australian market, service businesses face unique pressures - from high labor costs to increasing customer expectations for instant responses. Our automation solutions are designed specifically to give local Sydney and interstate businesses a competitive edge by replacing manual work with intelligent systems.
              </p>
              <p className="text-slate-300 mb-8 leading-relaxed">
                We focus on the "low-hanging fruit" first: the repetitive tasks that take the most time but require the least amount of human decision-making. This includes data entry, quote follow-ups, and appointment scheduling. By automating these, you reclaim your evenings and focus on high-value work.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { title: "ABN Registered", text: "Local Australian accountability and support." },
                  { title: "No Lock-in", text: "You own your systems and your data." },
                  { title: "Custom Logic", text: "Built for your specific business workflow." },
                  { title: "Ready Support", text: "Sydney-based team for priority assistance." }
                ].map((item, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <h3 className="font-bold text-blue-400 flex items-center gap-2">
                       <SafeIcon icon={FiShield} className="h-4 w-4" /> {item.title}
                    </h3>
                    <p className="text-sm text-slate-400">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm p-8 sm:p-12 rounded-3xl border border-white/10">
              <h2 className="text-xl font-bold mb-6 text-blue-400">Our Strategic Approach</h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                We don't just 'install software'. We consult with you to identify where your team is losing the most time. Whether it's <strong>enquiry automation for service businesses</strong> or <strong>workflow automation for trades Australia</strong>, we ensure the system is robust and scalable for long-term success.
              </p>
              <p className="text-slate-300 mb-8 leading-relaxed">
                Our goal is to help you replace spreadsheets with systems that actually talk to each other, saving you up to 15 hours of admin per week. This results in faster growth and a more sustainable business model.
              </p>
              <CTAButton className="w-full">Book Your Audit</CTAButton>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-10">Explore Our Expertise</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiTarget} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Home</h3>
              <p className="text-sm text-slate-500 mb-4">Return to our main dashboard and overview.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Go Home <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/about" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiLayers} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">About Us</h3>
              <p className="text-sm text-slate-500 mb-4">Learn about our Sydney-based automation team.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">About Team <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/blog" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiZap} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Automation Blog</h3>
              <p className="text-sm text-slate-500 mb-4">Read strategies for Australian small businesses.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Read Blog <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/contact" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Contact Us</h3>
              <p className="text-sm text-slate-500 mb-4">Start your free 20-minute strategy discussion.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Get in Touch <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Services;